import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { Logo } from "@/app/Logo";
import { ThemeToggle } from "@/app/shell/ThemeToggle";
import { brand } from "@/brand";
import { buttonClass } from "@/components/ui";
import { cn } from "@/lib/cn";

import { MENUS, TOP_LINKS, type NavItem } from "./links";
import { SiteLink } from "./SiteLink";
import { useTrials } from "./trials";
import { captureUtm, signupSearch } from "./utm";

/** One desktop dropdown: opens on hover and on click, closes on Escape or a click outside. */
function NavMenu({ label, items }: { label: string; items: NavItem[] }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex h-16 items-center gap-1 px-3 whitespace-nowrap text-[15px] font-medium text-fg transition-colors hover:text-brand",
          open && "text-brand",
        )}
      >
        {label}
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} aria-hidden />
      </button>
      <div
        id={panelId}
        hidden={!open}
        className="absolute top-full left-0 z-50 w-72 rounded-b-xl border border-border bg-surface p-2 shadow-lift"
      >
        <ul>
          {items.map((it) => (
            <li key={it.label}>
              <SiteLink
                to={it.to}
                onClick={() => setOpen(false)}
                className="block rounded-lg px-3 py-2.5 hover:bg-surface-2"
              >
                <span className="block text-sm font-medium text-fg">{it.label}</span>
                {it.hint ? <span className="mt-0.5 block text-xs text-muted">{it.hint}</span> : null}
              </SiteLink>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const STRIP_LINK =
  "inline-flex shrink-0 items-center gap-1 font-semibold text-ink-fg underline underline-offset-4 hover:no-underline";

/**
 * The thin strip above the header, on every public page: the member free trial
 * while it is on (Admin → Plans & trials), else the Research Analyst free trial
 * while that is on, else the tagline.
 */
function TrialStrip({ trial, raTrial }: { trial: number | null; raTrial: number | null }) {
  return (
    <div className="bg-ink text-ink-fg">
      <div className="site-wrap flex h-9 items-center justify-center gap-2 text-xs sm:text-[13px]">
        {trial ? (
          <>
            <p className="min-w-0 truncate">
              <span className="sm:hidden">Launch offer: {trial} days free</span>
              <span className="hidden sm:inline">Come research and invest: start your {trial}-day free trial.</span>
            </p>
            <Link to="/signup" search={signupSearch()} className={STRIP_LINK}>
              Start free trial <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          </>
        ) : raTrial ? (
          <>
            <p className="min-w-0 truncate">
              <span className="sm:hidden">Research Analysts: {raTrial} days free</span>
              <span className="hidden sm:inline">
                Research Analysts: start with a {raTrial}-day free trial, no payment needed.
              </span>
            </p>
            <Link to="/for-research-analysts" className={STRIP_LINK}>
              See RA plans <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          </>
        ) : (
          <>
            <p className="min-w-0 truncate">Research smarter. Invest with confidence.</p>
            <Link to="/pricing" className={STRIP_LINK}>
              See plans <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Header for every signed-out page: the offer strip, then the logo,
 * dropdown menus, analyst search, Login and Sign up. Below lg the menus move
 * into a panel that opens under the bar.
 */
export function SiteHeader() {
  const menuId = useId();
  const location = useRouterState({ select: (s) => s.location.href });
  // The menu belongs to the page it was opened on, so navigating closes it.
  const [openAt, setOpenAt] = useState<string | null>(null);
  const open = openAt === location;
  const toggle = () => setOpenAt(open ? null : location);
  const close = () => setOpenAt(null);
  const { member: trial, ra: raTrial } = useTrials();
  // Keep a social post's utm_* tags for the sign-up this visit may lead to.
  useEffect(() => {
    captureUtm();
  }, []);

  return (
    <>
      <TrialStrip trial={trial} raTrial={raTrial} />
      <header className="sticky top-[var(--safe-top)] z-40 border-b border-border bg-surface">
        <div className="site-wrap flex h-16 items-center gap-2">
          <Link to="/" aria-label={`${brand.name} home`} className="shrink-0 rounded-lg">
            <Logo />
          </Link>
          <nav aria-label="Site" className="ml-4 hidden items-center lg:flex">
            {MENUS.map((m) => (
              <NavMenu key={m.label} label={m.label} items={m.items} />
            ))}
            {TOP_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="inline-flex h-16 items-center px-3 text-[15px] font-medium whitespace-nowrap text-fg hover:text-brand"
                activeProps={{ className: "text-brand" }}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <nav aria-label="Account" className="ml-auto flex items-center gap-1.5">
            <ThemeToggle />
            <Link
              to="/login"
              search={{ redirect: "/" }}
              className={cn(buttonClass("secondary", "md"), "hidden h-10 rounded-full px-5 sm:inline-flex")}
            >
              Login
            </Link>
            <Link to="/signup" search={signupSearch()} className={cn(buttonClass("primary", "md"), "h-10 rounded-full px-4 sm:px-5")}>
              {trial ? (
                <>
                  <span className="sm:hidden">Free trial</span>
                  <span className="hidden sm:inline">Start free trial</span>
                </>
              ) : (
                "Join now"
              )}
            </Link>
            <button
              type="button"
              className="inline-flex size-10 items-center justify-center rounded-lg text-fg hover:bg-surface-2 lg:hidden"
              aria-expanded={open}
              aria-controls={menuId}
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={toggle}
            >
              {open ? <X className="size-5" aria-hidden /> : <Menu className="size-5" aria-hidden />}
            </button>
          </nav>
        </div>
        <div
          id={menuId}
          hidden={!open}
          className="max-h-[calc(100dvh-6.25rem)] overflow-y-auto border-t border-border bg-surface lg:hidden"
        >
          <nav aria-label="Site menu" className="site-wrap pt-3 pb-5">
            <div className="divide-y divide-border">
              {MENUS.map((m) => (
                <details key={m.label} className="group">
                  <summary className="flex cursor-pointer list-none items-center justify-between py-3.5 text-base font-medium text-fg [&::-webkit-details-marker]:hidden">
                    {m.label}
                    <ChevronDown className="size-4 text-muted transition-transform group-open:rotate-180" aria-hidden />
                  </summary>
                  <ul className="pb-2">
                    {m.items.map((it) => (
                      <li key={it.label}>
                        <SiteLink to={it.to} onClick={close} className="block rounded-lg px-3 py-2.5 text-sm text-muted hover:bg-surface-2 hover:text-fg">
                          {it.label}
                        </SiteLink>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
              {TOP_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={close} className="block py-3.5 text-base font-medium text-fg">
                  {l.label}
                </Link>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Link to="/login" search={{ redirect: "/" }} onClick={close} className={cn(buttonClass("secondary", "lg"), "rounded-full")}>
                Login
              </Link>
              <Link to="/signup" search={signupSearch()} onClick={close} className={cn(buttonClass("primary", "lg"), "rounded-full")}>
                {trial ? "Start free trial" : "Join now"}
              </Link>
            </div>
          </nav>
        </div>
      </header>
    </>
  );
}
